import React, { useState, useCallback } from "react"
import PropTypes from "prop-types"
import { Button, Tag, Space, Spin, Flex, Modal, Grid, Drawer } from "antd"
import { PlusOutlined, EditOutlined, DeleteOutlined, CloseOutlined } from "@ant-design/icons"
import { useDispatch } from "react-redux"
import { createGroup, removeGroup, updateGroup } from "store/clients"
import { getGroups } from "store/groups"
import { serialize } from "object-to-formdata"
import styles from "./index.module.css"
import { useTranslation } from "react-i18next"
import GroupForm from "./GroupForm"
import { Text, Title } from "ui/Typography"
import WarningOutlinedWithBg from "ui/WarningOutlinedWithBg"
import { UnsavedChangesModal } from "ui/UnsavedChangesModal"

const GroupMenuItems = ({
  action = "",
  groups = [],
  client,
  selectedGroups = [],
  onSelectedGroupsChange,
  moduleName,
  setSelectedClientIds,
  setDropdownVisible,
  onUnsavedChangesChange
}) => {
  const dispatch = useDispatch()
  const [loading, setLoading] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [adding, setAdding] = useState(false)
  const [editing, setEditing] = useState(null)
  const [newName, setNewName] = useState("")
  const [selectedColor, setSelectedColor] = useState("")
  const [deleteModalVisible, setDeleteModalVisible] = useState(false)
  const [groupToDelete, setGroupToDelete] = useState(null)
  const [showUnsavedModal, setShowUnsavedModal] = useState(false)
  const [addGroupValidation, setAddGroupValidation] = useState({
    name: false,
    color: false,
    nameMessage: "",
    colorMessage: "",
    message: ""
  })
  const { t } = useTranslation()

  const handleSelectGroup = useCallback(
    async (e, group) => {
      e.stopPropagation()
      const arr = selectedGroups?.flatMap((g) => g.group_id ?? g.id) || []
      if (arr.includes(group.id)) return
      const groupClone = { ...group }
      groupClone.group_id = groupClone?.id
      if (client?.id) {
        setSelectedClientIds?.([client.id])
      }
      const groupIds = selectedGroups?.flatMap((g) => g.group_id) || []
      if (!groupIds.includes(groupClone.group_id)) {
        onSelectedGroupsChange?.([...selectedGroups, groupClone])
      }
    },
    [selectedGroups, client?.id, setSelectedClientIds, onSelectedGroupsChange]
  )

  const handleAddClick = (e) => {
    e.stopPropagation()
    setAdding(true)
    isMobile && setDropdownVisible?.(false)
  }

  const handleCancelEdit = (e) => {
    e?.stopPropagation?.()
    setAdding(false)
    setEditing(null)
    setNewName("")
    setSelectedColor("")
    setShowUnsavedModal(false)
    setAddGroupValidation({
      name: false,
      color: false,
      nameMessage: "",
      colorMessage: "",
      message: ""
    })
  }

  const handleSaveGroup = useCallback(
    async (e) => {
      e?.stopPropagation()
      setAddGroupValidation({
        name: false,
        color: false,
        nameMessage: "",
        colorMessage: "",
        message: ""
      })
      if (!newName.trim() && !selectedColor) {
        setAddGroupValidation({
          name: !newName.trim(),
          color: !selectedColor,
          nameMessage: t("clients.group_name_required"),
          colorMessage: t("clients.group_color_required")
        })
        return
      }
      if (!newName.trim()) {
        setAddGroupValidation({
          name: !newName.trim(),
          nameMessage: t("clients.group_name_required")
        })
        return
      }
      if (!selectedColor) {
        setAddGroupValidation({
          color: !selectedColor,
          colorMessage: t("clients.group_color_required")
        })
        return
      }
      setLoading(true)
      try {
        if (editing) {
          const updatedGroup = { name: newName, color: selectedColor }
          await dispatch(updateGroup(editing.id, { group: updatedGroup }))
        } else {
          const formData = serialize({
            group: {
              name: newName,
              color: selectedColor
            }
          })
          await dispatch(createGroup(formData))
        }
        await dispatch(getGroups())
        handleCancelEdit(e)
      } catch (err) {
        console.error("Failed to save group", err)
      } finally {
        setLoading(false)
      }
    },
    [newName, selectedColor, t, editing, dispatch]
  )

  const handleEditClick = (e, group) => {
    e.stopPropagation()
    setEditing(group)
    setNewName(group.name)
    setSelectedColor(group.color)
    setAdding(true)
    setAddGroupValidation({
      name: false,
      color: false,
      nameMessage: "",
      colorMessage: "",
      message: ""
    })
    isMobile && setDropdownVisible?.(false)
  }

  const hasUnsavedChanges = editing
    ? newName !== (editing?.name || "") || selectedColor !== (editing?.color || "")
    : Boolean(newName.trim() || selectedColor)

  React.useEffect(() => {
    onUnsavedChangesChange?.(hasUnsavedChanges)
  }, [hasUnsavedChanges, onUnsavedChangesChange])

  const handleRequestClose = (e) => {
    e?.stopPropagation?.()

    if (hasUnsavedChanges) {
      setShowUnsavedModal(true)
      return
    }

    handleCancelEdit()
  }

  const handleDiscardChanges = () => {
    handleCancelEdit()
  }

  const handleDeleteClick = (e, group) => {
    e.stopPropagation()
    setGroupToDelete(group)
    setDeleteModalVisible(true)
  }
  const handleDeleteConfirm = useCallback(async () => {
    if (!groupToDelete) return

    setDeleteLoading(true)
    try {
      await dispatch(removeGroup(groupToDelete.id))
      await dispatch(getGroups())

      // Remove from selected groups if needed
      if (selectedGroups.includes(groupToDelete.id)) {
        const updated = selectedGroups.filter((id) => id.id !== groupToDelete.id)
        onSelectedGroupsChange?.(updated)
      }

      setDeleteModalVisible(false)
      setGroupToDelete(null)
    } catch (err) {
      console.error("Failed to delete group", err)
    } finally {
      setDeleteLoading(false)
    }
  }, [groupToDelete, dispatch, selectedGroups, onSelectedGroupsChange])

  const handleNameChange = (e) => {
    const value = e.target.value
    setNewName(value)
    if (value.trim().length < 1 || value.trim().length > 50) {
      setAddGroupValidation({
        name: true,
        message: t("clients.group_name_length")
      })
    } else {
      setAddGroupValidation({
        name: false,
        message: ""
      })
    }
  }

  const { useBreakpoint } = Grid
  const screens = useBreakpoint()
  const isMobile = !screens.md

  return (
    <>
      <div className={styles.customDropdown}>
        <div className={`${styles.groupItems} ${moduleName}`}>
          {groups?.filter(Boolean).map((group) => {
            const isSelected = selectedGroups.some((selectedGroup) => selectedGroup?.group_id === group?.id)
            return (
              <React.Fragment key={group?.id}>
                <button
                  onClick={(e) => handleSelectGroup(e, group)}
                  className={`w-full ${styles.groupButton} ${isSelected ? styles.disabledGroupButton : ""}`}
                  disabled={isSelected}
                >
                  <Tag
                    color={group.color}
                    style={{
                      color: group.color,
                      backgroundColor: group.color + "26",
                      opacity: isSelected ? 0.5 : 1,
                      cursor: isSelected ? "not-allowed" : "pointer"
                    }}
                    className={styles.groupTag}
                  >
                    {group.name}
                  </Tag>
                  {action !== "bulk_add" && (
                    <Space size={15}>
                      <DeleteOutlined
                        style={{ cursor: isSelected ? "not-allowed" : "pointer" }}
                        className={isSelected ? "opacity-50" : ""}
                        color="#525455"
                        onClick={(e) => handleDeleteClick(e, group)}
                      />
                      <EditOutlined
                        style={{ cursor: isSelected ? "not-allowed" : "pointer" }}
                        className={isSelected ? "opacity-50" : ""}
                        color="#525455"
                        onClick={(e) => handleEditClick(e, group)}
                      />
                    </Space>
                  )}
                </button>
                {editing &&
                  editing.id === group.id &&
                  (isMobile ? (
                    <Drawer
                      title={t("clients.edit_group", { name: group.name })}
                      open={true}
                      placement="bottom"
                      closable={false}
                      height="auto"
                      onClose={handleRequestClose}
                      bodyStyle={{ padding: 16 }}
                    >
                      <GroupForm
                        newName={newName}
                        handleNameChange={handleNameChange}
                        addGroupValidation={addGroupValidation}
                        selectedColor={selectedColor}
                        setSelectedColor={setSelectedColor}
                        handleCancelEdit={handleRequestClose}
                        handleSaveGroup={handleSaveGroup}
                      />
                    </Drawer>
                  ) : (
                    <GroupForm
                      newName={newName}
                      handleNameChange={handleNameChange}
                      addGroupValidation={addGroupValidation}
                      selectedColor={selectedColor}
                      setSelectedColor={setSelectedColor}
                      handleCancelEdit={handleRequestClose}
                      handleSaveGroup={handleSaveGroup}
                    />
                  ))}
              </React.Fragment>
            )
          })}
        </div>
        {!editing &&
          adding &&
          (isMobile ? (
            <Drawer title={t("clients.add_new_group")} open={true} placement="bottom" closable={false} height="auto" onClose={handleRequestClose}>
              <GroupForm
                newName={newName}
                handleNameChange={handleNameChange}
                addGroupValidation={addGroupValidation}
                selectedColor={selectedColor}
                setSelectedColor={setSelectedColor}
                handleCancelEdit={handleRequestClose}
                handleSaveGroup={handleSaveGroup}
              />
            </Drawer>
          ) : (
            <GroupForm
              newName={newName}
              handleNameChange={handleNameChange}
              addGroupValidation={addGroupValidation}
              selectedColor={selectedColor}
              setSelectedColor={setSelectedColor}
              handleCancelEdit={handleRequestClose}
              handleSaveGroup={handleSaveGroup}
            />
          ))}

        {!adding && (
          <Button color="primary" variant="text" onClick={handleAddClick} className="align-center mt-xs gap-xxs">
            <PlusOutlined />
            <span>{t("clients.add_new_group")}</span>
          </Button>
        )}

        {loading && <Spin className={`position-absolute ${styles.centerLoader}`} />}
      </div>

      <Modal
        centered
        open={deleteModalVisible}
        onCancel={() => setDeleteModalVisible(false)}
        width={431}
        title={
          <Flex justify="space-between" align="center" className="pb-s">
            <div className="flex-1 text-center">
              <WarningOutlinedWithBg />
            </div>
            <Button type="text" onClick={() => setDeleteModalVisible(false)} className={`${styles.closeButton} ml-auto p-0`}>
              <CloseOutlined />
            </Button>
          </Flex>
        }
        footer={null}
        closeIcon={null}
      >
        <Space className="w-full" direction="vertical" size={24}>
          <Flex vertical gap={16}>
            <Title level={3}>{t("clients.delete_group")}?</Title>
            <Text className="block text-left">
              Are you sure you want to delete group <b> {groupToDelete?.name}</b> ?
            </Text>
          </Flex>
          <Flex justify="left" gap={12} className={`mt-s ${styles.formModalFooter}`}>
            <Button onClick={() => setDeleteModalVisible(false)}>{t("global.cancel")}</Button>
            <Button danger type="primary" onClick={handleDeleteConfirm}>
              {t("global.delete")}
            </Button>
          </Flex>
        </Space>
        {deleteLoading && <Spin className={`position-absolute ${styles.centerLoader}`} />}
      </Modal>

      <UnsavedChangesModal open={showUnsavedModal} onDiscard={handleDiscardChanges} onKeepEditing={() => setShowUnsavedModal(false)} />
    </>
  )
}

GroupMenuItems.propTypes = {
  action: PropTypes.string,
  groups: PropTypes.arrayOf(PropTypes.object).isRequired,
  client: PropTypes.object,
  selectedGroups: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  onSelectedGroupsChange: PropTypes.func,
  setSelectedClientIds: PropTypes.func,
  moduleName: PropTypes.string,
  setDropdownVisible: PropTypes.func,
  onUnsavedChangesChange: PropTypes.func
}

export default GroupMenuItems
